import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { sb, User, Company } from './services/supabase';
import { ToastContainer } from './components/ui';
import './index.css';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import { Users, Billing, OtherPages } from './pages/OtherPages';
import Sidebar from './components/Sidebar';
import CompanyAdminSidebar from './components/CompanyAdminSidebar';

import CompanyDashboard from './pages/company/CompanyDashboard';
import CompanyAttendance from './pages/company/CompanyAttendance';
import CompanyEmployees from './pages/company/CompanyEmployees';
import { CompanyLeaves, LeaveSetup } from './pages/company/LeaveManagement';
import { CompanyReports, CompanyShifts, CompanyLocations } from './pages/company/CompanyOther';
import KioskManagement from './pages/company/KioskManagement';
import { Payroll } from './pages/company/Payroll';

type AppState = 'loading' | 'unauthenticated' | 'platform_admin' | 'company_admin';

function PlatformAdminShell({ currentUser }: { currentUser: User }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', height: '100vh', overflow: 'hidden' }}>
      <Sidebar user={currentUser} />
      <main style={{ overflowY: 'auto', background: 'var(--bg)' }}>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/users" element={<Users />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/usage" element={<OtherPages />} />
          <Route path="/activity" element={<OtherPages />} />
          <Route path="/settings" element={<OtherPages />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function CompanyAdminShell({ currentUser, company }: { currentUser: User; company: Company }) {
  const isManager = currentUser.role === 'manager';
  const hasLeave = company.feature_leave !== false;
  const hasPayroll = company.feature_payroll === true;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', height: '100vh', overflow: 'hidden' }}>
      <CompanyAdminSidebar user={currentUser} company={company} />
      <main style={{ overflowY: 'auto', background: 'var(--bg)' }}>
        <Routes>
          <Route path="/company/dashboard" element={<CompanyDashboard companyId={company.id} />} />
          <Route path="/company/attendance" element={<CompanyAttendance companyId={company.id} />} />
          <Route path="/company/employees" element={<CompanyEmployees companyId={company.id} userRole={currentUser.role} />} />
          <Route path="/company/leaves" element={<CompanyLeaves companyId={company.id} featureEnabled={hasLeave} />} />
          <Route path="/company/leave-setup" element={<LeaveSetup companyId={company.id} featureEnabled={hasLeave} />} />
          <Route path="/company/reports" element={<CompanyReports companyId={company.id} />} />
          {!isManager && <Route path="/company/shifts" element={<CompanyShifts companyId={company.id} />} />}
          {!isManager && <Route path="/company/locations" element={<CompanyLocations companyId={company.id} />} />}
          {!isManager && <Route path="/company/kiosk" element={<KioskManagement companyId={company.id} />} />}
          {!isManager && <Route path="/company/payroll" element={<Payroll companyId={company.id} featureEnabled={hasPayroll} />} />}
          <Route path="*" element={<Navigate to="/company/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  const [appState, setAppState] = useState<AppState>('loading');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);

  useEffect(() => {
    const { data: { subscription } } = sb.auth.onAuthStateChange(async (event, session) => {
      if (!session) { setAppState('unauthenticated'); return; }
      const { data: profile } = await sb.from('users').select('*, company:companies(*)').eq('id', session.user.id).single();
      if (!profile) { setAppState('unauthenticated'); return; }
      setCurrentUser(profile);
      if (['platform_admin', 'platform_staff'].includes(profile.role)) {
        setAppState('platform_admin');
      } else if (['company_admin', 'manager'].includes(profile.role) && profile.company) {
        setCompany(profile.company);
        setAppState('company_admin');
      } else {
        setAppState('unauthenticated');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  if (appState === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>⏱</div>
        <div style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 13 }}>Loading ClockedIQ...</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ToastContainer />
      <Routes>
        <Route path="/login" element={appState === 'unauthenticated' ? <Login /> : <Navigate to={appState === 'platform_admin' ? '/dashboard' : '/company/dashboard'} replace />} />
        {appState === 'platform_admin' && currentUser && (
          <Route path="/*" element={<PlatformAdminShell currentUser={currentUser} />} />
        )}
        {appState === 'company_admin' && currentUser && company && (
          <Route path="/*" element={<CompanyAdminShell currentUser={currentUser} company={company} />} />
        )}
        <Route path="*" element={<Navigate to={appState === 'unauthenticated' ? '/login' : appState === 'platform_admin' ? '/dashboard' : '/company/dashboard'} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
